<template>
   <v-container fluid class="receipt-page pa-0">
      <div class="receipt-shell">
         <!-- <header class="receipt-header">
            <div class="receipt-header__icon" aria-hidden="true">
               <v-icon color="white" size="30">receipt</v-icon>
            </div>
            <div>
               <div class="receipt-header__eyebrow">Kvitteringsarkiv</div>
               <h1>Send kvittering</h1>
               <p>Ta et bilde eller velg et du allerede har.</p>
            </div>
            <v-chip small color="blue lighten-4" text-color="blue darken-3">Frontend-mock</v-chip>
         </header> -->

         <v-card v-if="sentReceipt" class="receipt-card success-card" elevation="2">
            <div class="success-mark" aria-hidden="true">
               <v-icon color="white" size="42">check</v-icon>
            </div>
            <h2>Kvitteringen er sendt</h2>
            <p>E-posten ble sendt til</p>
            <strong>{{ sentReceipt.recipient }}</strong>
            <v-btn block large color="primary" class="mt-4" @click="startNewReceipt"> Send en ny kvittering </v-btn>
         </v-card>

         <v-card v-else class="receipt-card" elevation="2">
            <section class="capture-section">
               <div v-if="!imageUrl" class="capture-empty">
                  <div class="capture-empty__illustration" aria-hidden="true">
                     <v-icon size="54" color="blue darken-2">camera_alt</v-icon>
                  </div>
                  <h2>Legg til bilde</h2>
                  <!-- <p>Én kvittering per innsending.</p> -->

                  <v-btn block large color="primary" class="capture-action" @click="$refs.cameraInput.click()">
                     <v-icon left>camera_alt</v-icon>
                     Ta bilde
                  </v-btn>
                  <v-btn block large outline color="primary" class="capture-action" @click="$refs.libraryInput.click()">
                     <v-icon left>photo_library</v-icon>
                     Velg fra bilder
                  </v-btn>
               </div>

               <div v-else class="image-preview">
                  <img :src="imageUrl" alt="Forhåndsvisning av valgt kvittering" />
                  <div class="image-preview__footer">
                     <div class="image-preview__details">
                        <v-icon small color="green darken-2">check_circle</v-icon>
                        <span>{{ imageName }}</span>
                     </div>
                     <v-btn flat small color="primary" @click="removeImage">Bytt bilde</v-btn>
                  </div>
               </div>

               <input
                  ref="cameraInput"
                  class="visually-hidden"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  @change="selectImage"
               />
               <input ref="libraryInput" class="visually-hidden" type="file" accept="image/*" @change="selectImage" />
            </section>

            <v-divider></v-divider>

            <section class="receipt-form">
               <v-combobox
                  v-model="merchant"
                  :items="merchantNames"
                  label="Butikk (valgfritt)"
                  hint="Velg en tidligere butikk eller skriv inn en ny"
                  no-data-text="Skriv inn et nytt butikknavn"
                  prepend-icon="store"
                  clearable
                  persistent-hint
               ></v-combobox>

               <v-text-field
                  v-model="receiptDate"
                  type="date"
                  label="Kvitteringsdato"
                  prepend-icon="event"
                  :rules="dateRules"
               ></v-text-field>

               <v-textarea
                  v-model="comment"
                  label="Kommentar (valgfritt)"
                  prepend-icon="notes"
                  rows="2"
                  auto-grow
                  counter="240"
                  maxlength="240"
               ></v-textarea>
            </section>

            <v-alert :value="errorMessage" type="error" class="mx-3 mb-3">
               {{ errorMessage }}
            </v-alert>

            <section class="delivery-summary">
               <div>
                  <span class="delivery-summary__label">Sendes til</span>
                  <strong>{{ recipient }}</strong>
               </div>
               <!-- <div>
                  <span class="delivery-summary__label">Registrert av</span>
                  <strong>{{ userEmail }}</strong>
               </div> -->
            </section>

            <div class="send-section">
               <v-btn block large color="primary" :disabled="!canSend" :loading="sending" @click="submitReceipt">
                  <v-icon left>send</v-icon>
                  Send kvittering
               </v-btn>
               <p class="mock-note">Bildet sendes sikkert via myEconomy API.</p>
            </div>
         </v-card>
      </div>
   </v-container>
</template>

<script>
import { getMerchantSuggestions, RECEIPT_RECIPIENT, sendReceipt } from "./receiptService";

const MAX_IMAGE_SIZE = 20 * 1024 * 1024;

function todayAsInputValue() {
   const today = new Date();
   const year = today.getFullYear();
   const month = String(today.getMonth() + 1).padStart(2, "0");
   const day = String(today.getDate()).padStart(2, "0");
   return year + "-" + month + "-" + day;
}

function createSubmissionId() {
   if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
   }

   if (window.crypto && typeof window.crypto.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes)
         .map((value) => value.toString(16).padStart(2, "0"))
         .join("");
   }

   return "receipt_" + Date.now() + "_" + Math.random().toString(36).slice(2);
}

export default {
   name: "ReceiptCapture",
   data() {
      return {
         recipient: RECEIPT_RECIPIENT,
         image: null,
         imageUrl: "",
         merchant: "",
         merchants: [],
         receiptDate: todayAsInputValue(),
         comment: "",
         sending: false,
         errorMessage: "",
         sentReceipt: null,
         submissionId: createSubmissionId(),
         dateRules: [(value) => Boolean(value) || "Velg kvitteringsdato"],
      };
   },
   computed: {
      userEmail() {
         const user = this.$auth.getUser();
         return user && user.email ? user.email : "Innlogget bruker";
      },
      merchantNames() {
         return this.merchants.map((merchant) => merchant.name);
      },
      imageName() {
         return this.image ? this.image.name : "";
      },
      canSend() {
         return Boolean(this.image && this.receiptDate) && !this.sending;
      },
   },
   created() {
      this.loadMerchants();
   },
   beforeDestroy() {
      this.revokeImageUrl();
   },
   methods: {
      loadMerchants() {
         getMerchantSuggestions().then((merchants) => {
            this.merchants = merchants;
         });
      },
      selectImage(event) {
         const files = event.target.files;
         const image = files && files.length ? files[0] : null;
         event.target.value = "";
         this.errorMessage = "";

         if (!image) {
            return;
         }
         if (!image.type || image.type.indexOf("image/") !== 0) {
            this.errorMessage = "Filen må være et bilde.";
            return;
         }
         if (image.size > MAX_IMAGE_SIZE) {
            this.errorMessage = "Bildet er for stort. Velg et bilde som er mindre enn 20 MB.";
            return;
         }

         this.revokeImageUrl();
         this.image = image;
         this.imageUrl = URL.createObjectURL(image);
      },
      removeImage() {
         this.revokeImageUrl();
         this.image = null;
         this.errorMessage = "";
      },
      revokeImageUrl() {
         if (this.imageUrl) {
            URL.revokeObjectURL(this.imageUrl);
            this.imageUrl = "";
         }
      },
      submitReceipt() {
         if (!this.canSend) {
            return;
         }

         this.sending = true;
         this.errorMessage = "";
         sendReceipt({
            image: this.image,
            merchant: typeof this.merchant === "string" ? this.merchant : "",
            receiptDate: this.receiptDate,
            comment: this.comment,
            submissionId: this.submissionId,
         })
            .then((result) => {
               this.sentReceipt = result;
               this.loadMerchants();
               this.revokeImageUrl();
               this.image = null;
            })
            .catch((error) => {
               this.errorMessage = error.message || "Kvitteringen kunne ikke sendes. Prøv igjen.";
            })
            .finally(() => {
               this.sending = false;
            });
      },
      startNewReceipt() {
         this.sentReceipt = null;
         this.merchant = "";
         this.receiptDate = todayAsInputValue();
         this.comment = "";
         this.errorMessage = "";
         this.submissionId = createSubmissionId();
      },
   },
};
</script>

<style scoped>
.receipt-page {
   min-height: calc(100vh - 64px);
   min-height: calc(100dvh - 64px);
   background: #f3f7fb;
}

.receipt-shell {
   width: 100%;
   max-width: 620px;
   margin: 0 auto;
   padding: 28px 20px 48px;
}

.receipt-header {
   display: grid;
   grid-template-columns: auto 1fr auto;
   align-items: center;
   gap: 14px;
   margin-bottom: 18px;
}

.receipt-header__icon {
   display: flex;
   align-items: center;
   justify-content: center;
   width: 54px;
   height: 54px;
   border-radius: 17px;
   background: linear-gradient(145deg, #1565c0, #0d47a1);
   box-shadow: 0 8px 22px rgba(13, 71, 161, 0.22);
}

.receipt-header__eyebrow {
   color: #607d8b;
   font-size: 12px;
   font-weight: 700;
   letter-spacing: 0.08em;
   text-transform: uppercase;
}

.receipt-header h1 {
   margin: 1px 0 2px;
   color: #18324a;
   font-size: 28px;
   line-height: 1.15;
}

.receipt-header p {
   margin: 0;
   color: #607d8b;
}

.receipt-card {
   overflow: hidden;
   border-radius: 18px;
}

.capture-section,
.receipt-form,
.send-section {
   padding: 22px;
}

.capture-empty {
   text-align: center;
}

.capture-empty__illustration {
   display: flex;
   align-items: center;
   justify-content: center;
   width: 92px;
   height: 92px;
   margin: 0 auto 12px;
   border-radius: 50%;
   background: #e3f2fd;
}

.capture-empty h2,
.success-card h2 {
   margin: 0 0 4px;
   color: #18324a;
   font-size: 22px;
}

.capture-empty p {
   margin: 0 0 20px;
   color: #78909c;
}

.capture-action + .capture-action {
   margin-top: 10px;
}

.image-preview {
   overflow: hidden;
   border: 1px solid #d9e3ec;
   border-radius: 14px;
   background: #eef3f7;
}

.image-preview img {
   display: block;
   width: 100%;
   max-height: 440px;
   object-fit: contain;
}

.image-preview__footer {
   display: flex;
   align-items: center;
   justify-content: space-between;
   min-height: 52px;
   padding-left: 14px;
   background: white;
}

.image-preview__details {
   display: flex;
   min-width: 0;
   align-items: center;
   gap: 7px;
   color: #455a64;
}

.image-preview__details span {
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
}

.receipt-form {
   padding-bottom: 10px;
}

.delivery-summary {
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;
   margin: 0 22px;
   padding: 14px;
   border-radius: 12px;
   background: #f4f8fb;
}

.delivery-summary div {
   min-width: 0;
}

.delivery-summary__label {
   display: block;
   margin-bottom: 2px;
   color: #78909c;
   font-size: 12px;
}

.delivery-summary strong {
   display: block;
   overflow: hidden;
   color: #29465f;
   font-size: 13px;
   text-overflow: ellipsis;
   white-space: nowrap;
}

.send-section {
   padding-top: 18px;
}

.mock-note {
   margin: 10px 0 0;
   color: #78909c;
   font-size: 12px;
   text-align: center;
}

.success-card {
   padding: 38px 24px 24px;
   text-align: center;
}

.success-card p {
   margin: 12px 0 2px;
   color: #78909c;
}

.success-mark {
   display: flex;
   align-items: center;
   justify-content: center;
   width: 76px;
   height: 76px;
   margin: 0 auto 18px;
   border-radius: 50%;
   background: #2e7d32;
}

.visually-hidden {
   display: none;
}

@media (max-width: 600px) {
   .receipt-page {
      min-height: calc(100vh - 56px);
      min-height: calc(100dvh - 56px);
      background: #ffffff;
   }

   .receipt-shell {
      max-width: none;
      padding: 0 0 calc(12px + env(safe-area-inset-bottom));
   }

   .receipt-header {
      grid-template-columns: auto 1fr;
   }

   .receipt-header .v-chip {
      display: none;
   }

   .receipt-header__icon {
      width: 48px;
      height: 48px;
      border-radius: 15px;
   }

   .receipt-header h1 {
      font-size: 24px;
   }

   .receipt-header p {
      font-size: 13px;
   }

   .receipt-card {
      min-height: calc(100vh - 56px);
      min-height: calc(100dvh - 56px);
      border-radius: 0;
      box-shadow: none !important;
   }

   .capture-section,
   .receipt-form,
   .send-section {
      padding-right: 20px;
      padding-left: 20px;
   }

   .capture-section {
      padding-top: 22px;
      padding-bottom: 18px;
   }

   .capture-empty__illustration {
      width: 72px;
      height: 72px;
      margin-bottom: 8px;
   }

   .capture-empty__illustration .v-icon {
      font-size: 42px !important;
   }

   .capture-empty h2,
   .success-card h2 {
      font-size: 21px;
   }

   .capture-action {
      min-height: 52px;
      border-radius: 12px;
      box-shadow: none;
      font-size: 15px;
      letter-spacing: 0.02em;
   }

   .receipt-form {
      padding-top: 14px;
      padding-bottom: 4px;
   }

   .receipt-form input,
   .receipt-form textarea {
      font-size: 16px;
   }

   .delivery-summary {
      grid-template-columns: 1fr;
      margin-right: 20px;
      margin-left: 20px;
      padding: 12px 14px;
   }

   .send-section {
      position: sticky;
      z-index: 3;
      bottom: 0;
      padding-top: 14px;
      padding-bottom: calc(12px + env(safe-area-inset-bottom));
      background: rgba(255, 255, 255, 0.96);
      border-top: 1px solid #e4ebf1;
      box-shadow: 0 -8px 24px rgba(24, 50, 74, 0.08);
      backdrop-filter: blur(12px);
   }

   .send-section .v-btn {
      min-height: 52px;
      margin: 0;
      border-radius: 12px;
      box-shadow: none;
   }

   .mock-note {
      margin-top: 8px;
   }

   .success-card {
      padding-top: 48px;
      padding-bottom: calc(24px + env(safe-area-inset-bottom));
   }
}
</style>
